import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListChecks, Mail, Plus, Send, Trash2, Upload, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmailLogDialog } from "@/components/EmailLogDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OWNER_TYPES = ["admin", "business", "market"];
const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });
const ownerLabel = (ownerType) => {
  if (ownerType === "business") return "Businesses";
  if (ownerType === "market") return "Markets";
  return "Admin";
};
const formatEmailLogDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const listTypeLabel = (type) => {
  const labels = {
    admin_businesses: "Business profiles",
    admin_markets: "Market profiles",
    admin_shoppers: "Shopper accounts",
    admin_prospective_businesses: "Prospective businesses",
    admin_new_businesses: "Admin-created businesses",
    admin_custom: "Admin list",
    business_shopper: "Business shopper list",
    market_shopper: "Market shopper list",
  };
  return labels[type] || "Email list";
};
const canDeleteEmailList = (list) => Boolean(list);

export const EmailListsManagement = ({ token }) => {
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [campaignSends, setCampaignSends] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [members, setMembers] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [memberForm, setMemberForm] = useState({ email: "", name: "" });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [activeOwnerType, setActiveOwnerType] = useState("admin");
  const [selectedCampaignSend, setSelectedCampaignSend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);

  const selectedList = useMemo(
    () => lists.find((list) => list.id === selectedListId),
    [lists, selectedListId],
  );
  const groupedLists = useMemo(() => {
    const groups = { admin: [], business: [], market: [] };
    lists.forEach((list) => {
      const ownerType = list.owner_type === "business" || list.owner_type === "market" ? list.owner_type : "admin";
      groups[ownerType].push(list);
    });
    return groups;
  }, [lists]);
  const visibleLists = groupedLists[activeOwnerType] || [];
  const adminLists = groupedLists.admin || [];

  const adjustListCount = (listId, delta) => {
    if (!listId || !delta) return;
    setLists((previous) => previous.map((list) => (
      list.id === listId
        ? { ...list, active_member_count: Math.max(0, (list.active_member_count || 0) + delta) }
        : list
    )));
  };

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders(token);
      const [listsResponse, templatesResponse, contactsResponse, sendsResponse] = await Promise.all([
        axios.get(`${API}/admin/email-lists`, { headers }),
        axios.get(`${API}/admin/system-emails`, { headers }),
        axios.get(`${API}/admin/email-contacts`, { headers }),
        axios.get(`${API}/admin/email-campaign-sends`, { headers }),
      ]);
      const nextLists = listsResponse.data || [];
      setLists(nextLists);
      setTemplates(templatesResponse.data || []);
      setContacts(contactsResponse.data || []);
      setCampaignSends(sendsResponse.data || []);
      setSelectedListId((current) => current || (nextLists.find((list) => (list.owner_type || "admin") === "admin")?.id || nextLists[0]?.id || ""));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load email lists");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadMembers = useCallback(async (listId) => {
    if (!listId) {
      setMembers([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/email-lists/${listId}/members`, {
        headers: authHeaders(token),
      });
      setMembers(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load list members");
    }
  }, [token]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    loadMembers(selectedListId);
  }, [loadMembers, selectedListId]);

  useEffect(() => {
    if (!lists.length) return;
    const selectedOwnerType = selectedList?.owner_type === "business" || selectedList?.owner_type === "market" ? selectedList.owner_type : "admin";
    if (!selectedList || selectedOwnerType !== activeOwnerType) {
      setSelectedListId(visibleLists[0]?.id || "");
    }
  }, [activeOwnerType, lists.length, selectedList, visibleLists]);

  useEffect(() => {
    setRenameValue(selectedList?.name || "");
  }, [selectedList]);

  const selectOwnerType = (ownerType) => {
    setActiveOwnerType(ownerType);
    const nextList = groupedLists[ownerType]?.[0];
    setSelectedListId(nextList?.id || "");
  };

  const createList = async () => {
    if (!newListName.trim()) {
      toast.error("List name is required");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API}/admin/email-lists`, { name: newListName }, {
        headers: authHeaders(token),
      });
      setLists((previous) => [...previous, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedListId(response.data.id);
      setNewListName("");
      toast.success("Email list created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create list");
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    if (!selectedListId || !memberForm.email.trim()) {
      toast.error("Member email is required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/email-lists/${selectedListId}/members`, memberForm, {
        headers: authHeaders(token),
      });
      setMemberForm({ email: "", name: "" });
      adjustListCount(selectedListId, 1);
      await loadMembers(selectedListId);
      await loadLists();
      toast.success("Member added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setBusy(false);
    }
  };

  const uploadCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedListId) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/admin/email-lists/${selectedListId}/members/upload-csv`, formData, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      adjustListCount(selectedListId, response.data.added || 0);
      await loadMembers(selectedListId);
      await loadLists();
      toast.success(`CSV imported: ${response.data.added || 0} added, ${response.data.skipped || 0} skipped`);
      if (response.data.errors?.length) {
        toast.warning(`${response.data.errors.length} row(s) had issues`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    } finally {
      setUploadingCsv(false);
    }
  };

  const renameList = async () => {
    if (!selectedList || selectedList.type !== "admin_custom") return;
    if (!renameValue.trim()) {
      toast.error("List name is required");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.put(`${API}/admin/email-lists/${selectedList.id}`, { name: renameValue }, {
        headers: authHeaders(token),
      });
      setLists((previous) => previous.map((list) => list.id === selectedList.id ? response.data : list).sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Email list updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update list");
    } finally {
      setBusy(false);
    }
  };

  const deleteList = async () => {
    if (!canDeleteEmailList(selectedList)) return;
    if (!window.confirm(`Delete ${selectedList.name}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/email-lists/${selectedList.id}`, {
        headers: authHeaders(token),
      });
      const remaining = lists.filter((list) => list.id !== selectedList.id);
      setLists(remaining);
      const nextList = remaining.find((list) => (list.owner_type || "admin") === activeOwnerType) || remaining[0];
      setSelectedListId(nextList?.id || "");
      setMembers([]);
      toast.success("Email list deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete list");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (email) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/email-lists/${selectedListId}/members/${encodeURIComponent(email)}`, {
        headers: authHeaders(token),
      });
      adjustListCount(selectedListId, -1);
      await loadMembers(selectedListId);
      await loadLists();
      toast.success("Member removed from list");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    } finally {
      setBusy(false);
    }
  };

  const sendTemplate = async () => {
    if (!selectedListId || !selectedTemplateId) {
      toast.error("Choose a list and template");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API}/admin/email-lists/${selectedListId}/send-template`, {
        template_id: selectedTemplateId,
      }, { headers: authHeaders(token) });
      const result = response.data || {};
      const failures = result.failure_count || 0;
      const sent = result.success_count || 0;
      toast.success(failures ? `Email sent to ${sent} contacts; ${failures} failed.` : `Email sent successfully to ${sent} contacts.`);
      await loadLists();
      const sendsResponse = await axios.get(`${API}/admin/email-campaign-sends`, {
        headers: authHeaders(token),
      });
      setCampaignSends(sendsResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send template");
    } finally {
      setBusy(false);
    }
  };

  const assignContactToList = async (contact, listId) => {
    if (!listId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/email-contacts/assign`, {
        email: contact.email,
        name: contact.name || "",
        list_id: listId,
      }, { headers: authHeaders(token) });
      adjustListCount(listId, contact.list_ids?.includes(listId) ? 0 : 1);
      await loadMembers(selectedListId);
      await loadLists();
      toast.success(`${contact.email} assigned`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign email");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-gray-100 bg-white p-6 text-sm text-gray-500">Loading email lists...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Lists</h2>
            <p className="mt-1 text-sm text-gray-500">Lists are grouped by Admin, Businesses, and Markets. View members, send templates, and review email activity.</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
            <Input
              value={newListName}
              onChange={(event) => setNewListName(event.target.value)}
              placeholder="New admin list name"
              className="h-11 rounded-full"
            />
            <Button onClick={createList} disabled={busy} className="h-11 rounded-full bg-[#42D78A] px-5 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]">
              <Plus className="mr-2 h-4 w-4" /> Create
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700">
            <ListChecks className="h-4 w-4 text-[#42D78A]" /> Lists
          </div>
          <div className="mb-4 grid grid-cols-3 gap-2">
            {OWNER_TYPES.map((ownerType) => (
              <button
                key={ownerType}
                type="button"
                onClick={() => selectOwnerType(ownerType)}
                className={`rounded-full px-3 py-2 text-xs font-bold transition-all ${
                  activeOwnerType === ownerType
                    ? "bg-[#1a1a1a] text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-[#1a1a1a]"
                }`}
              >
                {ownerLabel(ownerType)}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {visibleLists.length === 0 ? (
              <p className="rounded-xl bg-gray-50 p-3 text-xs text-gray-500">No {ownerLabel(activeOwnerType).toLowerCase()} lists yet.</p>
            ) : visibleLists.map((list) => (
              <button
                key={list.id}
                type="button"
                onClick={() => setSelectedListId(list.id)}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  selectedListId === list.id ? "border-[#42D78A] bg-[#42D78A]/10" : "border-gray-100 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-[#1a1a1a]">{list.name}</p>
                    <p className="mt-1 text-xs text-gray-500">{listTypeLabel(list.type)}</p>
                  </div>
                  <Badge className="shrink-0 bg-gray-100 text-gray-700">{list.active_member_count || 0}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {selectedList?.name || "Select a list"}
                </h3>
                {selectedList && <p className="mt-1 text-sm text-gray-500">{listTypeLabel(selectedList.type)}</p>}
              </div>
              {selectedList && <Badge className="w-fit bg-[#42D78A]/15 text-[#157347]">{selectedList.active_member_count || 0} active</Badge>}
            </div>

            {selectedList && (
              <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto]">
                <div>
                  <Label>List name</Label>
                  <Input
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    className="mt-2"
                    disabled={selectedList.type !== "admin_custom"}
                  />
                </div>
                <Button onClick={renameList} disabled={busy || !renameValue.trim() || selectedList.type !== "admin_custom"} variant="outline" className="self-end rounded-full px-5">
                  Save Name
                </Button>
                <Button onClick={deleteList} disabled={busy} variant="ghost" className="self-end rounded-full text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
              <div>
                <Label>Email</Label>
                <Input value={memberForm.email} onChange={(event) => setMemberForm((previous) => ({ ...previous, email: event.target.value }))} className="mt-2" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={memberForm.name} onChange={(event) => setMemberForm((previous) => ({ ...previous, name: event.target.value }))} className="mt-2" />
              </div>
              <Button onClick={addMember} disabled={!selectedListId || busy} className="self-end rounded-full bg-[#1a1a1a] px-5 font-bold text-white">
                <Users className="mr-2 h-4 w-4" /> Add
              </Button>
              <label className={`inline-flex h-10 cursor-pointer items-center justify-center self-end rounded-full border border-gray-200 px-5 text-sm font-bold text-gray-700 transition-colors hover:border-[#42D78A] hover:text-[#1a1a1a] ${uploadingCsv || !selectedListId ? "pointer-events-none opacity-50" : ""}`}>
                <Upload className="mr-2 h-4 w-4" />
                {uploadingCsv ? "Uploading..." : "Upload CSV"}
                <input type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" disabled={uploadingCsv || !selectedListId} />
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500">CSV headers can include email, name, first_name, and last_name.</p>

            <div className="mt-6 space-y-2">
              {members.length === 0 ? (
                <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No members on this list yet.</p>
              ) : members.map((member) => (
                <div key={`${member.list_id}-${member.email}`} className="flex flex-col gap-3 rounded-xl bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-bold text-[#1a1a1a]">{member.email}</p>
                    <p className="text-sm text-gray-500">{member.name || "No name"}{!member.subscribed ? " - unsubscribed" : ""}</p>
                  </div>
                  <Button onClick={() => removeMember(member.email)} disabled={busy} variant="ghost" size="sm" className="w-fit text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="mr-2 h-4 w-4" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#42D78A]" />
              <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Send Template</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
              <select
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                className="h-11 rounded-full border border-gray-200 bg-white px-4 text-sm"
              >
                <option value="">Choose a system email template</option>
                {templates.map((template) => (
                  <option key={template.id || template.key} value={template.id || template.key}>{template.name}</option>
                ))}
              </select>
              <Button onClick={sendTemplate} disabled={!selectedListId || !selectedTemplateId || busy} className="rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]">
                <Send className="mr-2 h-4 w-4" /> Send Template
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#42D78A]" />
                <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Log</h3>
              </div>
              <Badge className="bg-gray-100 text-gray-700">{campaignSends.length} sends</Badge>
            </div>
            <div className="space-y-2">
              {campaignSends.length === 0 ? (
                <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No emails sent yet.</p>
              ) : campaignSends.slice(0, 25).map((send) => (
                <button
                  key={send.id}
                  type="button"
                  onClick={() => setSelectedCampaignSend(send)}
                  className="grid w-full grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 text-left transition hover:-translate-y-0.5 hover:bg-gray-100 hover:shadow-sm xl:grid-cols-[minmax(0,1fr)_12rem_12rem_auto] xl:items-center"
                  data-testid={`admin-email-log-${send.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[#1a1a1a]">{send.subject || send.template_name || "Untitled email"}</p>
                    <p className="mt-1 text-sm text-gray-500">{send.list_name || "Unknown list"} - Click to view email</p>
                  </div>
                  <Badge className="w-fit bg-white text-gray-700 shadow-sm">{send.sender_type || "unknown"}</Badge>
                  <p className="text-sm text-gray-500">{formatEmailLogDate(send.created_at)}</p>
                  <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                    <Badge className="bg-white text-gray-700 shadow-sm">{send.success_count || 0}/{send.recipient_count || 0} sent</Badge>
                    <Badge className={send.status === "sent" ? "bg-[#42D78A]/15 text-[#157347]" : send.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}>
                      {send.status || "unknown"}
                    </Badge>
                  </div>
                  {send.failure_count > 0 && (
                    <p className="text-xs text-red-600 xl:col-span-4">{send.failure_count} failed. {send.failures?.[0]?.error || "Review delivery settings."}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>All Emails</h3>
            <p className="mt-1 text-sm text-gray-500">View every email in the system and manually assign it to an admin list.</p>
          </div>
          <Badge className="w-fit bg-gray-100 text-gray-700">{contacts.length} emails</Badge>
        </div>
        <div className="space-y-2">
          {contacts.length === 0 ? (
            <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No emails found yet.</p>
          ) : contacts.map((contact) => (
            <div key={contact.email} className="grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-4 lg:grid-cols-[minmax(0,1fr)_12rem_18rem] lg:items-center">
              <div className="min-w-0">
                <p className="break-all font-bold text-[#1a1a1a]">{contact.email}</p>
                <p className="mt-1 text-sm text-gray-500">{contact.name || "No name"}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className="w-fit bg-white text-gray-700 shadow-sm">{contact.registered_as}</Badge>
                {contact.status && <Badge className="w-fit bg-[#42D78A]/15 text-[#157347]">{contact.status}</Badge>}
              </div>
              <select
                value=""
                onChange={(event) => assignContactToList(contact, event.target.value)}
                disabled={busy}
                className="h-10 rounded-full border border-gray-200 bg-white px-4 text-sm"
              >
                <option value="">Assign to admin list</option>
                {adminLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {contact.list_ids?.includes(list.id) ? "[assigned] " : ""}{list.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
      <EmailLogDialog
        log={selectedCampaignSend}
        open={Boolean(selectedCampaignSend)}
        onOpenChange={(open) => {
          if (!open) setSelectedCampaignSend(null);
        }}
      />
    </div>
  );
};
